import type {
  EvaluatedFlags,
  FeatureFlagOverrideRow,
  FeatureFlagRow,
  FlagDefinition,
  FlagScope,
  FlagValue,
} from './types.js';

/**
 * Pure flag-evaluation functions (ADR-0022). No I/O — callers fetch the rows
 * (API: Postgres; manager: heartbeat payload / disk cache) and pass them in.
 *
 * Precedence, most specific wins:
 *   host override > team override > org-wide override > stage value > compiled default
 *
 * Anything malformed — wrong value type, enum member outside allowedValues,
 * override for a different org, row for an unregistered key, archived flag —
 * drops down to the next layer, ending at the compiled default.
 */

/** Validate a stored (jsonb) value against the definition's type. */
export function normalizeFlagValue(
  definition: FlagDefinition,
  raw: unknown,
): FlagValue | undefined {
  if (definition.flagType === 'boolean') {
    return typeof raw === 'boolean' ? raw : undefined;
  }
  return typeof raw === 'string' && definition.allowedValues.includes(raw)
    ? raw
    : undefined;
}

/**
 * Parse an env-var string into a flag value. Consumers use this to keep the
 * legacy env var as the highest-precedence operator override during
 * migration; an unparseable value is ignored (returns undefined) rather than
 * silently disabling the flag.
 */
export function coerceEnvValue(
  definition: FlagDefinition,
  raw: string | undefined,
): FlagValue | undefined {
  if (raw === undefined || raw === '') return undefined;
  if (definition.flagType === 'boolean') {
    const lowered = raw.trim().toLowerCase();
    if (lowered === 'true' || lowered === '1') return true;
    if (lowered === 'false' || lowered === '0') return false;
    return undefined;
  }
  return normalizeFlagValue(definition, raw.trim());
}

function overrideSpecificity(override: FeatureFlagOverrideRow): number {
  if (override.host_id !== null) return 2;
  if (override.team_id !== null) return 1;
  return 0;
}

function overrideMatchesScope(
  override: FeatureFlagOverrideRow,
  scope: FlagScope,
): boolean {
  if (!scope.organizationId || override.organization_id !== scope.organizationId) {
    return false;
  }
  if (override.team_id !== null) return override.team_id === scope.teamId;
  if (override.host_id !== null) return override.host_id === scope.hostId;
  return true;
}

/** Resolve a single flag for a scope. */
export function resolveFlag(
  definition: FlagDefinition,
  row: FeatureFlagRow | undefined,
  overrides: readonly FeatureFlagOverrideRow[],
  scope: FlagScope,
): FlagValue {
  // Archived = "stop resolving": stage value AND overrides are both inert.
  if (row?.archived_at) return definition.defaultValue;

  const applicable = overrides
    .filter(
      (override) =>
        override.flag_key === definition.key && overrideMatchesScope(override, scope),
    )
    .sort((a, b) => overrideSpecificity(b) - overrideSpecificity(a));

  for (const override of applicable) {
    const value = normalizeFlagValue(definition, override.value);
    if (value !== undefined) return value;
  }

  if (row) {
    const value = normalizeFlagValue(definition, row.value);
    if (value !== undefined) return value;
  }

  return definition.defaultValue;
}

/**
 * Evaluate every registered flag for a scope. The result covers exactly the
 * definitions passed in — DB rows for unregistered keys are ignored, and
 * registered flags with no rows resolve to their compiled defaults.
 */
export function evaluateFlags(
  definitions: readonly FlagDefinition[],
  rows: readonly FeatureFlagRow[],
  overrides: readonly FeatureFlagOverrideRow[],
  scope: FlagScope,
): EvaluatedFlags {
  const rowsByKey = new Map(rows.map((row) => [row.key, row]));
  const evaluated: EvaluatedFlags = {};
  for (const definition of definitions) {
    evaluated[definition.key] = resolveFlag(
      definition,
      rowsByKey.get(definition.key),
      overrides,
      scope,
    );
  }
  return evaluated;
}
